import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Bot,
  Workflow,
  History,
  Wallet,
  Inbox,
  Coins,
  BookOpen,
  LogOut,
  ArrowLeft,
  UserCog,
  PanelLeftClose,
} from 'lucide-react';
import { cn, formatUSD } from '../lib/utils';
import { approvalsService } from '../services/approvalsService';
import { budgetService } from '../services/budgetService';
import { usageService, type Wallet as WalletData } from '../services/usageService';
import { useAuth } from '../auth/AuthContext';
import { agentStudioPath } from '../lib/routes';
import { AGENT_STUDIO_PERMISSIONS as P, useAgentStudioPermissions } from '../lib/permissions';

const nav = [
  { to: agentStudioPath(), label: 'Dashboard', icon: LayoutDashboard, permission: P.dashboardView, end: true },
  { to: agentStudioPath('activities'), label: 'Minhas Atividades', icon: Inbox, permission: P.activitiesView, badgeKey: 'approvals' },
  { to: agentStudioPath('agents'), label: 'Agentes', icon: Bot, permission: P.agentsView },
  { to: agentStudioPath('flows'), label: 'Fluxos', icon: Workflow, permission: P.flowsView },
  { to: agentStudioPath('knowledge-bases'), label: 'Bases de Conhecimento', icon: BookOpen, permission: P.knowledgeBasesView },
  { to: agentStudioPath('runs'), label: 'Execuções', icon: History, permission: P.runsView },
  { to: agentStudioPath('budget'), label: 'Áreas & Budget', icon: Wallet, permission: P.budgetView },
  { to: agentStudioPath('users'), label: 'Usuários', icon: UserCog, permission: P.usersManage },
  { to: agentStudioPath('costs'), label: 'Custos', icon: Coins, permission: P.costsView },
] as const;

export function Sidebar() {
  const { user, logout } = useAuth();
  const { has } = useAgentStudioPermissions();
  const location = useLocation();
  const [pending, setPending] = useState(0);
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const canViewActivities = has(P.activitiesView);

  useEffect(() => {
    if (canViewActivities) {
      Promise.all([
        approvalsService.pendingCountForUser().catch(() => 0),
        budgetService.listPendingGrants().then((g) => g.length).catch(() => 0),
      ]).then(([a, g]) => setPending(a + g));
    } else {
      setPending(0);
    }
    usageService.wallet().then(setWallet).catch(() => setWallet(null));
  }, [location.pathname, canViewActivities]);

  const displayName = user?.name ?? 'Usuario';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  return (
    <aside
      className={cn(
        'flex shrink-0 flex-col overflow-x-hidden bg-[#00335d] text-white transition-[width] duration-300 ease-out',
        collapsed ? 'w-[72px]' : 'w-[265px]',
      )}
    >
      <div className="flex h-[60px] shrink-0 items-center justify-between px-4 pt-5">
        {collapsed ? (
          <div className="flex w-full items-center justify-center">
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl transition-colors duration-200 hover:bg-white/10"
              aria-label="Expandir menu"
            >
              <img
                src="/media/logos/logo-icon.png"
                alt="Compra Agora"
                className="h-10 w-10 object-contain"
              />
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center overflow-hidden transition-opacity duration-200">
              <img
                src="/media/logos/logo-agent-studio-wide.png"
                alt="Compra Agora Agent Studio"
                className="h-14 object-contain"
              />
            </div>
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-slate-300 transition-all duration-200 hover:bg-white/10 hover:text-white"
              aria-label="Recolher menu"
            >
              <PanelLeftClose size={18} />
            </button>
          </>
        )}
      </div>

      <nav className="flex-1 space-y-1 px-3 py-2">
        <div className="mb-3 border-b border-white/10 pb-3">
          <Link
            to="/home"
            title={collapsed ? 'Sistema Interno' : undefined}
            className={cn(
              'flex min-h-10 items-center rounded-xl text-sm font-medium text-white/75 transition-colors hover:bg-white/5 hover:text-white',
              collapsed ? 'justify-center px-0' : 'gap-3 px-3',
            )}
          >
            <ArrowLeft className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && <span className="flex-1">Sistema Interno</span>}
          </Link>
        </div>

        {nav.filter((item) => has(item.permission)).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={'end' in item ? item.end : false}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              cn(
                'flex min-h-10 items-center rounded-xl text-sm font-medium transition-colors',
                collapsed ? 'justify-center px-0' : 'gap-3 px-3',
                isActive
                  ? 'bg-brand-orange text-white'
                  : 'text-white/70 hover:bg-white/5 hover:text-white',
              )
            }
          >
            <item.icon className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && <span className="flex-1">{item.label}</span>}
            {!collapsed && 'badgeKey' in item && item.badgeKey === 'approvals' && pending > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-orange px-1.5 text-[11px] font-semibold text-white">
                {pending}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {wallet && !collapsed && (
        <div className="border-t border-white/10 px-5 py-3">
          <div className="flex items-center justify-between text-[11px] text-white/60">
            <span className="flex items-center gap-1.5"><Coins className="h-3.5 w-3.5" /> Seu saldo</span>
            <span className={cn('font-semibold', wallet.available <= 0 ? 'text-brand-orange' : 'text-white')}>{formatUSD(wallet.available)}</span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10">
            <div
              className={cn('h-full rounded-full', wallet.available <= 0 ? 'bg-brand-orange' : 'bg-success')}
              style={{ width: `${wallet.monthlyBudget > 0 ? Math.min(100, Math.round((wallet.monthlyRemaining / wallet.monthlyBudget) * 100)) : (wallet.available > 0 ? 100 : 0)}%` }}
            />
          </div>
          <div className="mt-1 text-[10px] text-white/40">
            gasto: {formatUSD(wallet.monthlySpent)} - resta {formatUSD(wallet.monthlyRemaining)}/{formatUSD(wallet.monthlyBudget)}{wallet.topup > 0 ? ` - extra: ${formatUSD(wallet.topup)}` : ''}
          </div>
        </div>
      )}

      <div className={cn('border-t border-white/10 py-4', collapsed ? 'px-3' : 'px-5')}>
        {collapsed ? (
          <button
            type="button"
            onClick={() => logout()}
            title="Sair"
            className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <LogOut className="h-[18px] w-[18px]" />
          </button>
        ) : (
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-xs font-semibold">
              {initials || 'U'}
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-xs font-medium">{displayName}</div>
              <div className="truncate text-[11px] text-white/50">{user?.email ?? ''}</div>
            </div>
            <button
              type="button"
              onClick={() => logout()}
              title="Sair"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            >
              <LogOut className="h-[18px] w-[18px]" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
